import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";

import Home from "@/pages/Home";
import Heritage from "@/pages/Heritage";
import Accommodation from "@/pages/Accommodation";
import Dining from "@/pages/Dining";
import Activities from "@/pages/Activities";
import Orchard from "@/pages/Orchard";
import Gallery from "@/pages/Gallery";
import Contact from "@/pages/Contact";
import Booking from "@/pages/Booking";
import ManageBooking from "@/pages/ManageBooking";
import Banquet from "@/pages/Banquet";
import ClubTikratoli from "@/pages/ClubTikratoli";
import Roots from "@/pages/Roots";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import DayOutingFAB from "@/components/DayOutingFAB";
import DayOutingConfirmed from "@/pages/DayOutingConfirmed";

function SmoothScroll({ children }) {
  useEffect(() => {
    // Prevent the browser from restoring previous scroll position on navigation
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    // Expose so ScrollToTop can reset scroll on route change
    window.__lenis__ = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => {
      lenis.destroy();
      if (window.__lenis__ === lenis) delete window.__lenis__;
    };
  }, []);
  return children;
}

/**
 * ScrollToTop — resets scroll position to the top on every route change.
 * Uses Lenis' scrollTo when available, and a raw window.scrollTo fallback.
 * If the URL has a `#hash`, we honour it and don't jump to top.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let anchor links behave normally
    const lenis = window.__lenis__;
    if (lenis && typeof lenis.scrollTo === "function") {
      lenis.scrollTo(0, { immediate: true, force: true });
    }
    // Belt-and-braces — also reset native scroll (covers pages before Lenis rehydrates)
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname, hash]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Nav />
      {children}
      <Footer />
      <CallButton />
      <WhatsAppButton />
      <DayOutingFAB />
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <SmoothScroll>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster theme="dark" position="bottom-right" />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/heritage" element={<PublicLayout><Heritage /></PublicLayout>} />
            <Route path="/accommodation" element={<PublicLayout><Accommodation /></PublicLayout>} />
            <Route path="/dining" element={<PublicLayout><Dining /></PublicLayout>} />
            <Route path="/activities" element={<PublicLayout><Activities /></PublicLayout>} />
            <Route path="/orchard" element={<PublicLayout><Orchard /></PublicLayout>} />
            <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/booking" element={<PublicLayout><Booking /></PublicLayout>} />
            <Route path="/manage-booking" element={<PublicLayout><ManageBooking /></PublicLayout>} />
            <Route path="/banquet" element={<PublicLayout><Banquet /></PublicLayout>} />
            <Route path="/club-tikratoli" element={<PublicLayout><ClubTikratoli /></PublicLayout>} />
            <Route path="/roots" element={<PublicLayout><Roots /></PublicLayout>} />
            <Route path="/day-outing/confirmed/:id" element={<PublicLayout><DayOutingConfirmed /></PublicLayout>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </SmoothScroll>
    </div>
  );
}

export default App;
